import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  FieldArrayWithId,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { MAX_ITEM_QTY } from "../../../constants";
import {
  paymentLinkStatus,
  productStatus,
  subscriptionPeriod,
} from "../../../enums";

import {
  useDebounce,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../hooks";
import {
  ICreateEditPaymentLink,
  ICreateEditPaymentLinkLineItem,
  IFormWithTableProps,
  IHookForm,
  IModalFormWithTableProps,
  IPageAndSearchRequest,
  IViewBankAccount,
  IViewPaymentLink,
  IViewProduct,
  PagedMeta,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";

import { View } from "./View";
import { createEditPaymentLinkSchema } from "./createEditPaymentLinkSchema";

export interface ICreateEditPaymentLinkFormViewProps
  extends IHookForm<ICreateEditPaymentLink>,
    IModalFormWithTableProps {
  // payout tags
  isLoadingBankAccounts: boolean;
  bankAccountsInTagList: IViewBankAccount[];
  bankAccountsNotInTagList: IViewBankAccount[];
  payoutTagsFields: FieldArrayWithId<
    ICreateEditPaymentLink,
    "transaction_payout_tags",
    "id"
  >[];
  appendPayoutTagField: UseFieldArrayAppend<
    ICreateEditPaymentLink,
    "transaction_payout_tags"
  >;
  removePayoutTagField: UseFieldArrayRemove;
  onToggleSetSplitType: () => void;

  // line items
  lineItemsFields: FieldArrayWithId<
    ICreateEditPaymentLink,
    "line_items",
    "id"
  >[];
  appendNewPaymentLinkLineItemsField: () => void;
  removePaymentLinkLineItemsField: UseFieldArrayRemove;
  resetLineItemsToDefault: (index: number) => void;
  formValues: ICreateEditPaymentLink;
  isLoadingProducts: boolean;
  products: IViewProduct[];
  isEditMode: boolean;
}

export const defaultPaymentLinkLineItem: ICreateEditPaymentLinkLineItem = {
  // id: null,
  product: "",
  description: "",
  qty: 1,
  min_qty: 0,
  max_qty: MAX_ITEM_QTY,
  is_qty_adjustable_by_customer: false,
};

const newPaymentLink: ICreateEditPaymentLink = {
  // id: null,
  payment_link_name: null,
  friendly_url: null,

  currency: "",
  status: paymentLinkStatus.active,
  line_items: [],
  disabled_gateways: [],

  enable_instant_settlement_providers_only: false,
  transaction_payout_tags: [],

  partial_payment_enabled: false,
  max_partial_payment_count: 0,

  // other options
  is_donation_payment_link: false,
  collect_tax_automatically: false,
  require_customer_full_name: false,
  require_customer_address: false,
  require_customer_phone_number: false,
  allow_promo_codes: false,
  allow_business_customers_to_provide_tax_id: false,
  save_payment_for_future_use: false,
  // xxxxxx: false,

  // social media links
  enable_social_media_links: false,
  instagram_url: null,
  facebook_url: null,
  whatsapp_url: null,

  // verification
  enable_developer_options: false,
  live_webhook_url: null,
  test_webhook_url: null,

  // temp ui helpers
  uses_payout_tag_percentage: true,
};

export const CreateEditPaymentLinkForm = (
  props: IFormWithTableProps & {
    goBackOnClose: boolean; // NOTE: used for on-the-fly paymentLink creation eg while creating a product
  }
) => {
  const { navigate, navigateToRoutePreserveParams } = useReactRouterLink();
  const location = useLocation();

  const { id } = useParams();
  const isEditMode = !!id;

  const pageRoute = isEditMode ? `${id}/edit` : "create";

  const closeModal = () => {
    if (props.goBackOnClose) navigate(-1);
    else
      navigateToRoutePreserveParams(
        location.pathname.replace(`/${pageRoute}`, "")
      );
  };

  const debouncePeriodElapsed = useDebounce(100);

  const {
    isLoading: isLoadingProducts,
    isSuccess: isSuccessProducts,
    data: products,
    // meta: productsMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & {
      status: productStatus;
    },
    IViewProduct[],
    PagedMeta
  >(
    [
      "getProducts",
      {
        page: 1,
        page_size: 5000,
        search: "",
        status: productStatus.active,
      },
    ],
    {
      // enabled: debouncePeriodElapsed,
    }
  );

  const {
    isLoading: isLoadingModalData,
    isSuccess: isSuccessModalData,
    isError: isErrorModalData,
    data: paymentLinkToEdit,
  } = useMerchantApiQueryWithParams<{ id: string }, IViewPaymentLink>(
    [
      "getPaymentLinkById",
      {
        id: id!, // NOTE: use this to ensure that react query caches with the correct queryKey
      },
    ],
    {
      enabled: isEditMode && isSuccessProducts,
    },
    (urlTemplate) => urlTemplate.replace("id", id!)
  );

  useEffect(() => {
    // list of products is also required in edit mode
    if (isSuccessModalData && isSuccessProducts) {
      console.log("resetting form with data: ", paymentLinkToEdit);

      reset({
        ...paymentLinkToEdit,
        uses_payout_tag_percentage:
          paymentLinkToEdit?.transaction_payout_tags.find(
            (tg) => tg.percentage !== null
          ) !== undefined,
      });

      // BUG: this adds a persistent first item in list
      // paymentLinkToEdit?.line_items.forEach((item, index) => {
      //   console.log("setting line item for index: ", index);
      //   appendPaymentLinkLineItemsField(item);
      // });
    }
  }, [isSuccessModalData, isSuccessProducts]);

  const { isSuccess: isSuccessCreatePaymentLink, mutate: createPaymentLink } =
    // create new mode
    useMerchantApiMutation<ICreateEditPaymentLink, IViewPaymentLink>(
      "createPaymentLink"
    ).usePostMutation;

  const { isSuccess: isSuccessEditPaymentLink, mutate: editPaymentLink } =
    // if edit mode, use put mutation, else use post for creation
    useMerchantApiMutation<ICreateEditPaymentLink, IViewPaymentLink>(
      "editPaymentLink",
      [],
      {},
      (urlTemplate) => urlTemplate.replace("id", id!)
    ).usePutMutation;

  useEffect(() => {
    if (isSuccessCreatePaymentLink || isSuccessEditPaymentLink) {
      props.refreshTable();
      closeModal();

      reset(); // clear form
    }
  }, [isSuccessCreatePaymentLink, isSuccessEditPaymentLink]);

  const initialFormValues: ICreateEditPaymentLink = newPaymentLink;

  const handleApiSubmit = (data: ICreateEditPaymentLink) => {
    isEditMode ? editPaymentLink(data) : createPaymentLink(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, watch, control, reset, setValue } =
    useHookForm({
      initialFormValues,
      validationSchema: createEditPaymentLinkSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    });

  console.warn("payment link form errors: ", errors);

  const {
    fields: lineItemsFields,
    append: appendPaymentLinkLineItemsField,
    remove: removePaymentLinkLineItemsField,
  } = useFieldArray({ name: "line_items", control });

  const formValues = watch();

  const productsOfSameCurrency = products?.filter(
    (p) => p.currency === formValues.currency
  );

  const appendDefaultPaymentLinkLineItemsField = () =>
    appendPaymentLinkLineItemsField(defaultPaymentLinkLineItem);

  const resetLineItemsToDefault = (index: number) => {
    removePaymentLinkLineItemsField(index);
    appendDefaultPaymentLinkLineItemsField();
  };

  useEffect(() => {
    formValues.currency &&
      reset({
        ...formValues,
        line_items: [defaultPaymentLinkLineItem],
      });

    // insert first line item when currency selected
    // if (formValues.line_items.length == 0) {
    //   if (formValues.currency) {
    //     appendDefaultPaymentLinkLineItemsField();
    //   }
    // }
  }, [formValues.currency]);

  // payout tags
  const {
    isLoading: isLoadingBankAccounts,
    isSuccess: isSuccessBankAccounts,
    data: withdrawalBankAccounts,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IViewBankAccount[],
    PagedMeta
  >([
    "getBankAccounts",
    {
      page: 1,
      page_size: 5000,
      search: null,
    },
  ]);

  const {
    fields: payoutTagsFields,
    append: appendPayoutTagField,
    remove: removePayoutTagField,
  } = useFieldArray({ name: "transaction_payout_tags", control });

  const bankAccountsNotInTagList =
    withdrawalBankAccounts?.filter((bnk) =>
      bnk.bank_account_tags.every((tag) =>
        payoutTagsFields.every((potag) => tag.id !== potag.payout_tag)
      )
    ) || [];

  const bankAccountsInTagList =
    withdrawalBankAccounts?.filter((bnk) =>
      bnk.bank_account_tags.find((tag) =>
        payoutTagsFields.find((potag) => tag.id === potag.payout_tag)
      )
    ) || [];

  // Clear all %s/amounts in tags when split mode is toggled
  const onToggleSetSplitType = () => {
    var isCurrentlyUsingPerc =
      formValues.transaction_payout_tags.find(
        (tg) => tg.percentage !== null
      ) !== undefined;

    var isCurrentlyUsingAmt =
      formValues.transaction_payout_tags.find((tg) => tg.amount !== null) !==
      undefined;

    var resetRequired = isCurrentlyUsingPerc || isCurrentlyUsingAmt;

    if (
      !resetRequired ||
      window.confirm(
        `You are about to change your payout split type. This will clear all the data for ${
          formValues.uses_payout_tag_percentage ? "amount" : "percentages"
        } that you have entered. Proceed?`
      )
    ) {
      setValue(
        "uses_payout_tag_percentage",
        !formValues.uses_payout_tag_percentage
      );
      setValue(
        "transaction_payout_tags",
        formValues.transaction_payout_tags.map((tg) => ({
          ...tg,
          amount: formValues.uses_payout_tag_percentage ? null : tg.amount,
          percentage: !formValues.uses_payout_tag_percentage
            ? null
            : tg.percentage,
        }))
      );
    }
  };

  return (
    <View
      {...props}
      isEditMode={isEditMode}
      isModalOpen={isEditMode ? !!id : true}
      isLoadingModalData={isLoadingModalData || isLoadingProducts}
      isErrorModalData={isErrorModalData}
      closeModal={closeModal}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      formValues={formValues}
      // payout tags
      isLoadingBankAccounts={isLoadingBankAccounts}
      bankAccountsInTagList={bankAccountsInTagList}
      bankAccountsNotInTagList={bankAccountsNotInTagList}
      payoutTagsFields={payoutTagsFields}
      appendPayoutTagField={appendPayoutTagField}
      removePayoutTagField={removePayoutTagField}
      onToggleSetSplitType={onToggleSetSplitType}
      // line items
      lineItemsFields={lineItemsFields}
      appendNewPaymentLinkLineItemsField={
        appendDefaultPaymentLinkLineItemsField
      }
      removePaymentLinkLineItemsField={removePaymentLinkLineItemsField}
      resetLineItemsToDefault={resetLineItemsToDefault}
      isLoadingProducts={isLoadingProducts}
      products={productsOfSameCurrency || []}
    />
  );
};
